import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../apiClient";
import { notificationKeys } from "./queryKeys";

export type Notification = {
  id: number;
  user_id: number;
  type: string;
  title: string;
  body: string;
  data: {
    payment_id?: number;
    payment_external_id?: string;
    customer_organization_id?: number;
    note_id?: number;
    customer_name?: string;
  } | null;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
};

type UnreadCountResponse = {
  count: number;
};

type MarkReadResponse = {
  success: boolean;
  notification_id: number;
};

type MarkAllReadResponse = {
  success: boolean;
  updated_count: number;
};

export const useNotificationsQuery = (enabled: boolean = true) =>
  useQuery({
    queryKey: notificationKeys.list(),
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<Notification[]>(
        "/notifications/?limit=20",
      );
      return data;
    },
  });

export const useUnreadCountQuery = () =>
  useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const { data } = await apiClient.get<UnreadCountResponse>(
        "/notifications/unread-count",
      );
      return data;
    },
    // Refetch on window focus to keep badge updated
    refetchOnWindowFocus: true,
  });

export const useMarkNotificationReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: number) => {
      const { data } = await apiClient.post<MarkReadResponse>(
        `/notifications/${notificationId}/read`,
      );
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: notificationKeys.list() }),
        queryClient.invalidateQueries({
          queryKey: notificationKeys.unreadCount(),
        }),
      ]);
    },
  });
};

export const useMarkAllNotificationsReadMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<MarkAllReadResponse>(
        "/notifications/read-all",
      );
      return data;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: notificationKeys.list() }),
        queryClient.invalidateQueries({
          queryKey: notificationKeys.unreadCount(),
        }),
      ]);
    },
  });
};
